// "KI-Zugang" (?view=ai): connects AI tools (Claude, ChatGPT, Copilot, own agents) to Lanewerk via
// MCP (server: lanewerk-server mcp.rs, POST /mcp). With accounts an app signs in through the
// browser (OAuth, server oauth.rs; the person allows it on the "App verbinden" page, connect.ts) and
// is listed here under "Verbundene Apps" (GET/DELETE /api/me/connections); tools without a browser
// sign-in use personal access tokens (GET/POST/DELETE /api/me/tokens; the raw token is shown once).
// Without accounts (local use) no sign-in is needed, but the endpoint answers on localhost only.
import { api, type Session } from "./account";

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

interface Token { id: string; name: string; created: number; lastUsed: number | null }
interface Connection { id: string; client: string; from?: string | null; created: number; lastUsed: number | null }

const dateFmt = new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Copies `text`; the button says whether it worked (blocked clipboard: select the field instead). */
async function copy(text: string, btn: HTMLButtonElement, field?: HTMLInputElement | HTMLElement) {
  try {
    await navigator.clipboard.writeText(text);
    btn.textContent = "Kopiert";
  } catch {
    if (field instanceof HTMLInputElement) field.select();
    else if (field) getSelection()?.selectAllChildren(field);
    btn.textContent = "Mit ⌘C kopieren";
  }
}

function copyRow(value: string, label: string, id?: string) {
  const row = el("div", "pp-link-row");
  const f = el("input", "field");
  f.readOnly = true;
  f.value = value;
  f.setAttribute("aria-label", label);
  if (id) f.id = id;
  const b = el("button", undefined, "Kopieren");
  b.type = "button";
  b.addEventListener("click", () => void copy(value, b, f));
  row.append(f, b);
  return row;
}

function code(text: string, label: string) {
  const box = el("div", "ai-code");
  const pre = el("pre");
  pre.textContent = text;
  pre.setAttribute("aria-label", label);
  const b = el("button", "ai-code-copy", "Kopieren");
  b.type = "button";
  b.addEventListener("click", () => void copy(text, b, pre));
  box.append(pre, b);
  return box;
}

/** Setup lines for the common clients: Claude Code for this person (`--scope user`, the token
 *  stays out of projects), and a config entry that takes the token from an environment variable
 *  (such files often end up in a repository). */
export function setup(url: string, token: string | null) {
  const header = token ? ` --header "Authorization: Bearer ${token}"` : "";
  const entry = token ? { type: "http", url, headers: { Authorization: "Bearer ${LANEWERK_TOKEN}" } } : { type: "http", url };
  return {
    claudeCode: `claude mcp add --transport http --scope user lanewerk ${url}${header}`,
    json: JSON.stringify({ mcpServers: { lanewerk: entry } }, null, 2),
  };
}

/** A list of things a person can end ("Widerrufen" / "Trennen"). */
function revocable<T extends { id: string; created: number; lastUsed: number | null }>(items: T[], title: (t: T) => string, action: string, onEnd: (t: T) => void) {
  const ul = el("ul", "ai-tokens");
  for (const t of items) {
    const li = el("li", "ai-token");
    const info = el("div", "ai-token-info");
    info.append(el("strong", undefined, title(t)), el("span", "menu-hint",
      `seit ${dateFmt.format(new Date(t.created))} · ${t.lastUsed ? `zuletzt benutzt ${dateFmt.format(new Date(t.lastUsed))}` : "noch nie benutzt"}`));
    const x = el("button", "pp-link-btn", action);
    x.type = "button";
    x.setAttribute("aria-label", `${title(t)}: ${action}`);
    x.addEventListener("click", () => onEnd(t));
    li.append(info, x);
    ul.appendChild(li);
  }
  return ul;
}

export class AiAccess {
  private root = document.getElementById("lw-ai") as HTMLElement;
  private tokens: Token[] = [];
  private connections: Connection[] = [];
  private fresh: { token: string; name: string; id: string } | null = null;
  private message = "";
  readonly url = new URL("./mcp", location.href).href.replace(/\?.*$/, "");

  constructor(private session: Session) {
    document.title = "KI-Zugang – Lanewerk";
    this.render();
    if (session.user) void this.load();
  }

  private async load() {
    try {
      [this.tokens, this.connections] = await Promise.all([api<Token[]>("./api/me/tokens"), api<Connection[]>("./api/me/connections")]);
      this.message = "";
    } catch (e) {
      this.message = `Die Zugänge konnten nicht geladen werden (${(e as Error).message}).`;
    }
    this.render();
  }

  private async create(name: string) {
    try {
      const r = await api<{ token: string; info: Token }>("./api/me/tokens", "POST", { name });
      this.fresh = { token: r.token, name: r.info.name, id: r.info.id };
      this.message = "";
    } catch (e) {
      this.message = `Kein Zugang erstellt (${(e as Error).message}).`;
    }
    await this.load();
    this.root.querySelector<HTMLButtonElement>("#lw-ai-fresh button")?.focus();
  }

  private async disconnect(c: Connection) {
    try {
      await api(`./api/me/connections/${encodeURIComponent(c.id)}`, "DELETE");
    } catch (e) {
      this.message = `Nicht getrennt (${(e as Error).message}).`;
    }
    await this.load();
  }

  private async revoke(t: Token) {
    try {
      await api(`./api/me/tokens/${encodeURIComponent(t.id)}`, "DELETE");
      if (this.fresh?.id === t.id) this.fresh = null;
    } catch (e) {
      this.message = `Nicht widerrufen (${(e as Error).message}).`;
    }
    await this.load();
  }

  private render() {
    const local = !this.session.auth;
    const head = el("div", "home-head");
    head.appendChild(el("h1", undefined, "KI-Zugang"));
    const lead = el("p", "ai-lead",
      "Verbinde KI-Werkzeuge wie Claude, ChatGPT, Copilot oder Cursor über MCP (Model Context Protocol) mit Lanewerk. " +
      "Die KI liest deine Boards und plant Karten mit deinen Rechten: Karten anlegen, ändern, verschieben, Abhängigkeiten setzen – löschen kann sie nichts. " +
      "Alle sehen die Änderungen sofort; im Verlauf der Karte steht „(KI)“ hinter deinem Namen.");
    const parts: HTMLElement[] = [head, lead];

    const addr = el("section", "card-page ai-section");
    addr.append(el("h2", "ai-h2", "Adresse des MCP-Servers"), copyRow(this.url, "Adresse des MCP-Servers", "lw-ai-url"));
    if (local) addr.appendChild(el("p", "home-hint", "Lanewerk läuft ohne Konten: Es braucht keinen Zugangsschlüssel, der MCP-Server antwortet dafür nur auf diesem Rechner (localhost)."));
    parts.push(addr);

    if (!local) {
      const sign = el("section", "card-page ai-section");
      sign.id = "lw-ai-oauth";
      sign.append(
        el("h2", "ai-h2", "Mit Anmeldung verbinden (empfohlen)"),
        el("p", undefined, "Die App fragt bei Lanewerk an, du meldest dich im Browser an und erlaubst es – ganz ohne Schlüssel zum Kopieren. Die Verbindung erscheint dann unten und lässt sich jederzeit trennen."),
        el("p", "ai-step", "claude.ai (Web, Desktop, Mobile):"),
        el("p", undefined, "Einstellungen → Konnektoren → eigenen Konnektor hinzufügen, die Adresse oben eintragen und „Verbinden“ wählen. Dafür muss Lanewerk aus dem Internet erreichbar sein (claude.ai verbindet sich von seinen Servern aus)."),
        el("p", "ai-step", "Claude Code (Terminal):"),
        code(setup(this.url, null).claudeCode, "Befehl für Claude Code mit Anmeldung"),
        el("p", "home-hint", "Danach in Claude Code „/mcp“ → lanewerk → anmelden. Andere Apps (Cursor, VS Code …) bieten die Anmeldung an, sobald sie die Adresse kennen."),
      );
      const apps = el("section", "card-page ai-section");
      apps.id = "lw-ai-apps";
      apps.appendChild(el("h2", "ai-h2", "Verbundene Apps"));
      apps.appendChild(this.connections.length
        ? revocable(this.connections, (c) => (c.from ? `${c.client} (${c.from})` : c.client), "Trennen", (c) => void this.disconnect(c))
        : el("p", "home-hint", "Noch keine App verbunden."));
      parts.push(sign, apps);
      const sec = el("section", "card-page ai-section");
      sec.appendChild(el("h2", "ai-h2", "Zugangsschlüssel"));
      sec.appendChild(el("p", undefined, "Für Werkzeuge ohne Anmeldung im Browser (eigene Agenten, Skripte): ein persönlicher Schlüssel pro Gerät oder Werkzeug."));
      if (this.fresh) {
        const box = el("div", "pp-link");
        box.id = "lw-ai-fresh";
        box.setAttribute("role", "status");
        const p = el("p");
        p.append("Schlüssel für ", el("strong", undefined, this.fresh.name), " – nur jetzt sichtbar. Behandle ihn wie ein Passwort; wer ihn hat, handelt in deinem Namen.");
        box.append(p, copyRow(this.fresh.token, "Zugangsschlüssel", "lw-ai-token"));
        sec.appendChild(box);
      }
      const form = el("form", "ai-new");
      const name = el("input", "field");
      name.id = "lw-ai-name";
      name.maxLength = 60;
      name.placeholder = "Wofür, z. B. Claude auf dem MacBook";
      name.setAttribute("aria-label", "Name des Zugangs");
      const make = el("button", "primary", "Zugang erstellen");
      make.type = "submit";
      form.append(name, make);
      form.addEventListener("submit", (e) => { e.preventDefault(); void this.create(name.value); });
      sec.appendChild(form);
      if (this.tokens.length) {
        sec.appendChild(revocable(this.tokens, (t) => t.name, "Widerrufen", (t) => void this.revoke(t)));
      } else {
        sec.appendChild(el("p", "home-hint", "Noch kein Zugang. Erstelle einen pro Gerät oder Werkzeug – so kannst du einzelne wieder widerrufen."));
      }
      const msg = el("p", "home-status", this.message);
      msg.setAttribute("role", "alert");
      sec.appendChild(msg);
      parts.push(sec);
    }

    const how = el("section", "card-page ai-section");
    const token = local ? null : this.fresh?.token ?? "DEIN-SCHLÜSSEL";
    const s = setup(this.url, token);
    how.append(
      el("h2", "ai-h2", local ? "Einrichten" : "Einrichten mit Schlüssel"),
      el("p", "ai-step", "Claude Code (Terminal):"),
      code(s.claudeCode, "Befehl für Claude Code"),
      el("p", "ai-step", "Andere Werkzeuge mit MCP-Konfiguration (z. B. .mcp.json eines Projekts):"),
      code(s.json, "MCP-Konfiguration"),
      ...(local ? [] : [el("p", "home-hint", "Den Schlüssel dafür als Umgebungsvariable LANEWERK_TOKEN setzen – nicht in die Datei schreiben, sie landet oft in einem Repository. (Cursor schreibt ${env:LANEWERK_TOKEN}.)")]),
      el("p", "home-hint", "Danach z. B. fragen: „Was steht diese Woche bei mir an?“, „Plane die Inbetriebnahme in KW 47 in der Spur Software“ oder „Welche Abhängigkeiten im Board Orion sind im Konflikt?“"),
    );
    parts.push(how);
    this.root.replaceChildren(...parts);
  }
}
